import React from 'react';
import { Button, message } from 'antd';
import BulkEditor from '../BulkEditor';
import UseBktGrid from '../UseBktGrid';
import { HIDDEN_COLUMNS } from '../../constants/grid.constants';
import { NO_DATA_FOUND, EDIT_SUCCESS } from '../../constants/text.constants';
import defaultTheme from '../../styles';
import { plural, snakeToCamel, validateRow } from '../../helpers';
import { ShiftTask, GridWrapper, StyledIcon } from './styles';
import ShiftTime from './ShiftTime';

const DataPageGrid = ({
  persistParams,
  config,
  rows,
  lookups,
  gridApi,
  setShowHideColumnTreeToEnabled,
  meta,
  addLinkColumn,
  isNotEditable,
  isNotDeletable,
  addModal,
  onBulkEdit,
  bulkEditing,
  loading,
  checkedState,
  onSelect,
  page,
  updateConfig,
  gantt,
}) => {
  // Disables editing for rows where the column `editing_controlled_by` is not null.
  // Backend is returning snake cases here, nothing much frontend can do to the linting error.
  let nonEditableRowIds = [];
  let columnHideListFromToggle = [];
  if (meta && meta.currentMeta && meta.currentMeta.editing_controlled_by) {
    const {
      editing_controlled_by: {
        editable_if_link_status_equals, editable_if_column_is_empty,
      },
    } = meta.currentMeta;

    if (editable_if_column_is_empty) {
      nonEditableRowIds = (rows.results || rows)
        .filter(row => row[snakeToCamel(editable_if_column_is_empty)] !== '')
        .map(({ id }) => id);
    }

    if (editable_if_link_status_equals) {
      nonEditableRowIds = (rows.results || rows)
        .filter(row => row.linkStatus !== snakeToCamel(editable_if_link_status_equals))
        .map(({ id }) => id);
    }

    if (persistParams && Array.isArray(persistParams.dataPageHideColumns)) {
      columnHideListFromToggle = persistParams.dataPageHideColumns
        .filter(column => (column.split('-')[0] === meta.currentMeta.item_type))
        .map(column => (column.split('-')[1]));
    }
  }

  const gridConfig = {
    rowHeight: meta.currentMeta.item_type === 'shift_summary' ? 74 : 25,
    nonEditableRowIds,
    includeFilter: !meta.currentMeta.is_child && !bulkEditing,
    includeSort: !meta.currentMeta.is_child && !bulkEditing,
    includeEditDeleteCell: !bulkEditing && !isNotEditable,
    includeItemSelection: false,
    includeToolbar: false,
    includePagination: !meta.currentMeta.is_child && !bulkEditing,
    includeRowCounts: !meta.currentMeta.is_child && !bulkEditing,
    hiddenColumns: HIDDEN_COLUMNS.concat(columnHideListFromToggle),
    limit: page ? page.limit : null,
    offset: page ? page.offset : null,
    ordering: (page && page.ordering) ? page.ordering : null,
    filters: (page && page.filters) ? page.filters : {},
    paginationConfig: {
      pageOptions: [
        { value: 5, text: 5 },
        { value: 10, text: 10 },
        { value: 25, text: 25 },
        { value: 50, text: 50 },
        { value: 100, text: 100 },
        { value: 1000, text: 1000 },
        { value: 10000, text: 10000 },
      ],
      shiftOn: 4,
    },
  };

  const modalLink = (title, data) => data.content
    && (
      <Button
        disabled={bulkEditing}
        size="small"
        onClick={() => addModal(
          {
            title,
            data,
            editLockDown: nonEditableRowIds.find(id => data.row.id === id),
          },
        )}
      >
        {`${data.content.length} ${plural('item', data.content.length)}`}
      </Button>
    );

  const renderShift = (data, row, ganttMode) => {
    const placeShiftTime = ({ fromTime, toTime, tasks }) => (
      <ShiftTime
        dayStart={config.dayStart}
        unassigned={!row.employee}
        shift={{ fromTime, toTime, tasks }}
      />
    );

    const placeShiftTask = ({ fromTime, toTime, tasks }) => (
      <ShiftTask title={`${fromTime} - ${toTime}\n${tasks}`}>
        <div>
          <b>
            {`${fromTime} - ${toTime}`}
          </b>
        </div>
        <div>{tasks}</div>
      </ShiftTask>
    );

    if (Array.isArray(data)) {
      if (ganttMode) return (data.map(obj => placeShiftTime(obj)));
      return (data.map(obj => placeShiftTask(obj)));
    }

    // Backwards compatability.
    if (typeof data === 'object') {
      if (ganttMode) return (placeShiftTime(data));
      return (placeShiftTask(data));
    }

    return '';
  };

  const addCheckboxColumn = r => r.map(
    row => ({
      // Non-editable row shouldn't be selected for bulk edit and delete.
      __FUNC_COLUMN: !nonEditableRowIds.find(id => (row.id === id))
        ? { customUI: 'customCheckbox' }
        : (
          <StyledIcon
            type="lock"
            color="greyscale.2"
            mx={1}
          />
        ),
      ...row,
    }),
  );

  // Only allow selection if you are not in the process of bulk editing,
  // and the data is not editable.
  // For disabling editing for individual rows, please see const nonEditableRowIds.
  const canBeSelected = !bulkEditing
    && !isNotEditable;

  const withCheckboxes = dataRows => canBeSelected ? addCheckboxColumn(dataRows) : dataRows;

  const metaWithCheckboxes = useMeta => canBeSelected
    ? { ...useMeta, columns: [{ name: '__FUNC_COLUMN', display_name: '' }, ...useMeta.columns] }
    : useMeta;

  // Extend gridApi.put with validation and extra messaging capability after success.
  const extendPut = ({ data, successCallback }) => { // This only applies to Bkt-grid inline edit.
    const { put } = gridApi;
    validateRow(data, meta.currentMeta.columns)
      .then((valid) => {
        if (!valid) return false;
        put({
          data,
          successCallback: () => {
            message.success(EDIT_SUCCESS(meta.currentMeta.item_display_name));
            // In inline-editing nested data row, setShowHideColumnTreeToEnabled is undefined.
            // This is a quick fix addressing this issue and should not impact others.
            if (setShowHideColumnTreeToEnabled) setShowHideColumnTreeToEnabled();
            successCallback();
          },
        }, meta.currentMeta.item_type);
        return true;
      })
      .catch((error) => {
        message.error(error.message);
        return false;
      });
  };

  return (
    <GridWrapper>
      <UseBktGrid
        meta={metaWithCheckboxes(meta.currentMeta)}
        data={
          rows.results
            ? {
              ...rows,
              results: withCheckboxes(rows.results),
            }
            : withCheckboxes(rows)
        }
        lookups={lookups}
        addLinkColumn={addLinkColumn}
        config={gridConfig}
        onNested={modalLink}
        onGanttData={renderShift}
        api={{
          // Normally I'd use spread here but bkt-grid is being strange with the bulkDelete prop.
          get: gridApi.get,
          post: gridApi.post,
          put: extendPut,
        }}
        checkedState={checkedState}
        onSelect={onSelect}
        height={`calc(100% - ${defaultTheme.heights.navBarHeight})`}
        noDataMessage={NO_DATA_FOUND}
        updateConfig={updateConfig}
        isNotDeletable={isNotDeletable}
        gantt={gantt}
        className={(meta.currentMeta.item_type === 'shift_summary') && 'DCRPRemoveGapsInBktGrid'}
      />
      {bulkEditing
        && (
          <BulkEditor
            ids={rows.map(({ id }) => id)}
            loading={loading}
            onBulkEdit={onBulkEdit}
            meta={meta}
            lookups={lookups}
          />
        )
      }
    </GridWrapper>
  );
};

export default DataPageGrid;
